// --- The board binder (SC-2872) — bind-path resolution over bound query data ---
//
// The heart of the trusted renderer, mirroring the ekom-os BoardRenderer
// posture: bind paths NAME locations in bound data — `$q.<queryId>.<field…>` /
// `$row.<field…>` — and can never carry expressions (the Retool `{{ }}` trap).
// Nothing here evals, templates, or touches the DOM: every resolver returns
// data, and the node components put it on screen exclusively through text nodes
// (React text children — the no-innerHTML invariant has its own test).
//
// Hardening:
//   • Paths must match a FULLY ANCHORED grammar (`^…$`) — a path with anything
//    beyond `[A-Za-z0-9_]` segments resolves to undefined, not "best effort".
//   • The segment walk refuses `__proto__` / `constructor` / `prototype`, so a
//     malicious path can't climb into prototypes, and only traverses plain
//     data (objects/arrays) — never functions.
//   • Formatting coerces defensively: a non-finite "number" renders as its
//     string form, an unparsable "date" renders verbatim.

import type { BoardQueryResult, BoundValue, ValueFormat } from '../../types';

/** Resolution context: the bind's query results, plus (inside a row scope) the
 *  current row. Row scopes nest via `withRow` — `$q.` paths still resolve. */
export interface BindContext {
  queries: Record<string, BoardQueryResult>;
  row?: Record<string, unknown>;
}

/** A child context scoped to one row (Table/List/Chart iteration). */
export function withRow(ctx: BindContext, row: Record<string, unknown>): BindContext {
  return { queries: ctx.queries, row };
}

// Fully anchored: `$q.<id>` + zero-or-more `.<segment>`s, nothing else. `$row`
// may stand alone (the whole row) or carry segments.
//
// The segment charset MUST match the backend's `_SEGMENT_RE`
// (`^[A-Za-z0-9_:\-]+$`, app/boards/spec.py) — colons and hyphens included.
// Aggregate metric keys are `kind[:attribute[:window]]`, so every non-`count`
// metric binds through a colon-bearing segment (`$row.metrics.sum:AnnualRevenue`).
// A narrower charset here does not error: the path simply fails to match,
// `resolveBind` returns undefined, and the tile renders blank/0 — so a board the
// backend validated and persisted silently shows nothing. Query IDs stay on the
// narrow charset (they are authored, not derived).
//
// Widening is safe: `walk()` still refuses FORBIDDEN_SEGMENTS and own-property
// checks every hop, so no prototype-climbing segment becomes reachable.
const Q_PATH = /^\$q\.([A-Za-z0-9_]+)((?:\.[A-Za-z0-9_:-]+)*)$/;
const ROW_PATH = /^\$row((?:\.[A-Za-z0-9_:-]+)*)$/;

// Prototype-climbing segments are refused outright (walk returns undefined).
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);

function walk(base: unknown, dottedRest: string): unknown {
  let cur = base;
  for (const seg of dottedRest.split('.').filter(Boolean)) {
    if (FORBIDDEN_SEGMENTS.has(seg)) return undefined;
    if (cur === null || typeof cur !== 'object') return undefined;
    if (!Object.prototype.hasOwnProperty.call(cur, seg)) return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** Resolve a bind path against the context, or `undefined` for anything that
 *  isn't a well-formed path naming a present location. */
export function resolveBind(path: string, ctx: BindContext): unknown {
  const q = Q_PATH.exec(path);
  if (q) {
    // The query id is walked with the same discipline as segments: no
    // prototype-climbing ids, own properties only.
    if (FORBIDDEN_SEGMENTS.has(q[1])) return undefined;
    if (!Object.prototype.hasOwnProperty.call(ctx.queries, q[1])) return undefined;
    return walk(ctx.queries[q[1]], q[2]);
  }
  const r = ROW_PATH.exec(path);
  if (r) return ctx.row === undefined ? undefined : walk(ctx.row, r[1]);
  return undefined;
}

// A date-only wire value ("2026-07-30") gets a local-midnight anchor so the
// rendered day can't shift across timezones; full ISO timestamps parse as-is.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Format a resolved raw value for display. Returns '' for null/undefined —
 *  the components render nothing rather than "undefined". */
export function formatValue(raw: unknown, format?: ValueFormat): string {
  if (raw === null || raw === undefined) return '';
  switch (format) {
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n.toLocaleString() : String(raw);
    }
    case 'percent': {
      // The wire sends full-precision floats (avg_completeness 46.01033…) —
      // round to at most one decimal; `Number()` drops a trailing ".0".
      const n = Number(raw);
      return Number.isFinite(n) ? `${Number(n.toFixed(1))}%` : String(raw);
    }
    case 'date': {
      const s = String(raw);
      const d = new Date(DATE_ONLY.test(s) ? `${s}T00:00:00` : s);
      return Number.isNaN(d.getTime())
        ? s
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    default:
      return String(raw);
  }
}

/** Resolve + format a `BoundValue` (bind wins over an authored literal). */
export function displayValue(value: BoundValue | undefined, ctx: BindContext): string {
  if (!value) return '';
  if (typeof value.bind === 'string') return formatValue(resolveBind(value.bind, ctx), value.format);
  return value.text ?? '';
}

/** The rows a `rows:` bind path names, as an array of row objects (non-object
 *  members are dropped — a row scope must be walkable). Anything else → []. */
export function rowsOf(path: string, ctx: BindContext): Record<string, unknown>[] {
  const v = resolveBind(path, ctx);
  if (!Array.isArray(v)) return [];
  return v.filter(
    (r): r is Record<string, unknown> => r !== null && typeof r === 'object' && !Array.isArray(r),
  );
}

/** The per-query error for the query a path addresses, or `null`. `error` is the
 *  RESERVED key of the bind envelope (SC-2870): a string `error` on a query
 *  result means that query failed and its tiles render the error note. */
export function queryErrorOf(path: string, ctx: BindContext): string | null {
  const m = /^\$q\.([A-Za-z0-9_]+)/.exec(path);
  if (!m) return null;
  const q = ctx.queries[m[1]];
  if (q && typeof q === 'object' && typeof (q as { error?: unknown }).error === 'string') {
    return (q as { error: string }).error;
  }
  return null;
}

/** A raw numeric reading of a bind (meters, chart values): NaN-safe, default 0. */
export function numberOf(path: string, ctx: BindContext): number {
  const n = Number(resolveBind(path, ctx));
  return Number.isFinite(n) ? n : 0;
}
